import { NavigateFunction } from "react-router";

import { hideLoadingFrame } from "@covertree/new-business-redux";

import { setMode, type EndorsementMode } from "@redux/slices/endorsement";
import { useAppDispatch } from "@redux/hooks";

// lists
export const OVERVIEW_PATH = "overview";
export const POLICIES_PATH = "policies";
export const PENDING_CANCELLATION_PATH = "pending";
export const RENEWALS_PATH = "renewals";
export const SEQUOIA_PATH = "sequoia";
export const SEQUOIA_CONTINUE_PATH = `${SEQUOIA_PATH}/continue`;
export const QUOTES_PATH = "quotes";
export const QUOTES_CONTINUE_PATH = `${QUOTES_PATH}/continue`;
export const BULK_QUOTES_PATH = "bulk-quotes";
export const SEQUOIA_UPLOAD_PATH = "sequoia-upload";
export const SEQUOIA_STATUSES_PATH = "sequoia-statuses";
export const FAST_DECLINE_PATH = "fast-decline";
export const QUOTES_CONTINUE_FAST_DECLINE = `${OVERVIEW_PATH}/${FAST_DECLINE_PATH}`;

// new policy
export const BASE_NEW_POLICY_PATH = "create-policy";

export enum NewPolicyRoutes {
  ERROR = "error",
  FAST_DECLINE = "fast-decline",
  UNIT_COUNT = "unit-count",
  HOME_ADDRESS = "home-address",
  HOME_DETAILS = "home-details",
  SELECT_PRODUCT = "select-product",
  PERSONAL_DETAILS = "personal-details",
  PERSONALIZED_QUOTES = "personalized-quotes",
  UNDERWRITING_QUESTIONS = "underwriting",
  EXTRA_COVERAGES = "extra-coverages",
  CLAIMS_CHECK = "claims-check",
  CLAIMS_LIST = "claims-list",
  POLICY_SUMMARY = "policy-summary",
  CUSTOMIZE_POLICY = "customize-policy",
  DECLINE_PROPERTY = "decline-property",
  UNDERWRITING_REVIEW = "underwriting-review",
  CHECKOUT = "checkout",
  CONGRATULATIONS = "congratulations",
}

// helpers
export const getNewPolicyPath = (route: NewPolicyRoutes) => {
  return `/${BASE_NEW_POLICY_PATH}/${route}`;
};

/**
 * Navigates to a policy step
 * @param navigate                - Navigate function
 * @param to                      - Path to navigate to
 * @param currentPath             - Current path
 * @param dispatch                - Dispatch - if supplied close loading frame
 * @param additionalState         - Additional state to pass
 * @param replace                 - Replace the current history entry
 */
export const navigateToPolicyStep = (
  navigate: NavigateFunction,
  to: NewPolicyRoutes,
  currentPath: NewPolicyRoutes,
  dispatch?: ReturnType<typeof useAppDispatch>,
  state?: Record<string, unknown>,
  replace?: boolean
) => {
  navigate(getNewPolicyPath(to), {
    state,
    replace,
  });

  if (dispatch) {
    setTimeout(() => {
      dispatch(hideLoadingFrame());
    });
  }
};

// endorsement
export const BASE_ENDORSEMENT_PATH = "endorsement";

export enum EndorsementRoutes {
  INSURED_INFO = "insured-info",
  COVERAGE = "coverage",
  SUMMARY = "summary",
  UW_REVIEW = "uw-review",
  CHECKOUT = "checkout",
}

export type { EndorsementMode };

interface EndorsementPathParams {
  endorsementId?: string | null;
  tab?: string;
}

/**
 * Builds an endorsement step path, e.g.
 * `/policies/abc123/endorsement/insured-info`.
 */
export const getEndorsementPath = (
  policyLocator: string,
  route: EndorsementRoutes,
  { endorsementId, tab }: EndorsementPathParams = {}
) => {
  const base = `/${POLICIES_PATH}/${policyLocator}/${BASE_ENDORSEMENT_PATH}/${route}`;
  const search = new URLSearchParams();
  if (endorsementId) search.set("endorsementId", endorsementId);
  if (tab) search.set("tab", tab);
  const query = search.toString();
  return query ? `${base}?${query}` : base;
};

/**
 * Navigates to an endorsement step;
 * `mode`/`endorsementId` are carried as search params so they survive step
 * navigations.
 */
export const navigateToEndorsementStep = async (
  navigate: NavigateFunction,
  policyLocator: string,
  to: EndorsementRoutes,
  options: EndorsementPathParams & {
    mode?: EndorsementMode;
  } & {
    dispatch?: ReturnType<typeof useAppDispatch>;
    state?: Record<string, unknown>;
    replace?: boolean;
  } = {}
) => {
  const { dispatch, state, replace, mode, endorsementId, tab } = options;

  if (dispatch && mode) {
    await dispatch(setMode(mode));
  }

  navigate(getEndorsementPath(policyLocator, to, { endorsementId, tab }), {
    state,
    replace,
  });
};
